// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formFieldQtyFieldStyles } from "@store/Themes/common/mixins/formField";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, FC, KeyboardEvent, useState } from "react";
import { css } from "styled-components";

export interface GoToPageInputPresentationProps extends ExpandStyles<GoToPageInputStyles> {}

export interface GoToPageInputProps extends GoToPageInputPresentationProps {
    totalPages: number;
    currentPage: number;
    onGoToPage: (page: number) => void;
}

export interface GoToPageInputStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    textField?: TextFieldPresentationProps;
    goButton?: ButtonPresentationProps;
}

export const goToPageInputStyles: GoToPageInputStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(10)};
        `,
    },
    textField: {
        sizeVariant: "xsmall" as any,
        mergeCss: true,
        labelProps: {
            mergeCss: true,
            weight: 400,
            css: css`
                white-space: nowrap;
            `,
        },
        cssOverrides: {
            formField: css`
                width: auto;
            `,
            inputSelect: css`
                height: ${pxToRem(34)};
                ${formFieldQtyFieldStyles()}
            `,
        },
    },
    goButton: {
        variant: "tertiary",
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            min-width: auto;
        `,
    },
};

const GoToPageInput: FC<GoToPageInputProps> = ({
    totalPages,
    currentPage,
    onGoToPage,
    extendedStyles,
    overridedStyles,
}) => {
    const [inputValue, setInputValue] = useState("");
    const styles = useExpandProps(goToPageInputStyles, extendedStyles, overridedStyles);
    const parsedValue = parseInt(inputValue, 10);
    const targetPage = !Number.isNaN(parsedValue) && parsedValue > 0 ? Math.min(parsedValue, totalPages) : undefined;
    const canSubmit = !!targetPage && targetPage !== currentPage;

    const handleSubmit = () => {
        if (!targetPage || targetPage === currentPage) {
            return;
        }

        onGoToPage(targetPage);
        setInputValue("");
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if ([".", ",", "-", "+"].includes(event.key)) {
            event.preventDefault();
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            handleSubmit();
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.currentTarget.value;
        if (!nextValue || /^[1-9]\d*$/.test(nextValue)) {
            setInputValue(nextValue);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                {...styles.textField}
                type="number"
                inputMode="numeric"
                step={1}
                label={translate("{0}:", translate("Go to page"))}
                labelPosition="left"
                preventEllipsis={true}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                error=""
            />
            <Button {...styles.goButton} type="button" onClick={handleSubmit} disabled={!canSubmit}>
                {translate("Go")}
            </Button>
        </StyledWrapper>
    );
};

export default GoToPageInput;
